// ============================================================================
// ui/artwork-upload.js — put the buyer's two logo files somewhere linkable
// ============================================================================
// Spec: `Weight-Designer-Bot/LOGO-ARTWORK-RETENTION-SPEC.md`.
//
// TWO FILES, TWO JOBS (the spec's §2, restated because the labels on the vendor
// sheet depend on it being right):
//   • ORIGINAL — what production should print from. `logo-step.js` reassigns one
//     variable down the whole ingest chain (canvas re-encode at q=0.92 → a
//     Real-ESRGAN upscale that INVENTS detail → a transparent-margin trim), so for
//     a vector or high-res upload the processed copy is strictly worse than what we
//     were given. `ui/artwork-hold.js` is the only place the real bytes survive.
//   • ENHANCED — the contract. It is what the buyer saw on the plate and approved.
//
// ⚠ THE ORIGINAL IS A SESSION FACT, and every caller has to be built for its
// absence. The holder is module-scoped memory filled at UPLOAD, so a design opened
// from a share link, or resumed from an autosave, has an enhanced logo and no
// original — and no amount of asking will produce one, because the bytes were
// destroyed during ingest before any of this existed. The vendor sheet prints
// "Not on file" rather than a dead link; the quote path records `not-retained`.
//   ⭐ EXCEPT IN REP MODE, where `retainOriginalForRep` stores the file the moment
// it is uploaded and pins the signed PATH onto the design. The path survives a
// save, a reopen and a share link, so a rep opening last week's design still has
// a raw logo to hand the factory — see that function for why the buyer's rule is
// untouched by this.
//
// ⚠ DELIBERATE NEAR-DUPLICATE, and worth naming so the next person collapses them
// on purpose rather than by accident: `ui/product-step.js`'s `uploadArtwork()` does
// this same POST inside the quote submit. It is NOT extracted here because it does
// two more things this must not — it also ships the buyer's own attachments, and it
// returns the `originalStatus` vocabulary the LEAD carries ('stored' /
// 'upload-failed' / 'not-retained' / a worker reason), which a rep reads when
// deciding whether to chase a customer for a file. Merging them would put the
// lead's contract in the path of a PDF button. What IS shared is the endpoint and
// the two-file rule above.
// ============================================================================

import { retained } from './artwork-hold.js';
import { isRepMode } from '../rep-mode.js';

const ARTWORK_ENDPOINT = 'https://rfq-worker.ryan-29b.workers.dev/wd-artwork';

/**
 * Retain the ORIGINAL as soon as it is uploaded, and remember WHERE — rep mode only.
 *
 * ⛔ THE HOLDER IS CLEARED WHEN A DIFFERENT DESIGN IS OPENED, and it has to be: it stops customer
 * A's file riding onto customer B's quote (`main.js`, `clearDesignIdentity`). The consequence is
 * that a rep who opens a saved design has an enhanced logo and no original — so the Vendor Design
 * Sheet had nothing to link to on exactly the workflow it exists for (Ryan 2026-08-27: *"No raw
 * logo still on the vendor sheet"*). Retaining at SUBMIT cannot help either: a rep-built design is
 * never submitted as a customer quote.
 *
 * ⚠ REP MODE ONLY, and that is the retention rule intact rather than an exception to it.
 * `LOGO-ARTWORK-RETENTION-SPEC.md`: *"the original is retained at SUBMIT, so a browsing session that
 * never asks for a price leaves nothing behind."* That protects a BUYER who is only looking. A rep
 * uploading a logo into the portal is doing our work, on our tool, on a design they intend to save —
 * there is no browsing session to protect. A buyer's path is untouched.
 *
 * What is stored on the design is a signed PATH, never the bytes — see `serializeLogo`.
 * Best-effort and silent: a rep must never be stopped from designing because R2 was slow.
 */
export async function retainOriginalForRep(state) {
  if (!isRepMode()) return false;
  const held = retained();
  if (!held) return false;
  const profile = state && state.designProfile;
  if (!profile) return false;
  // Already pinned this file — a relayout or a re-render must not mint a second copy in R2.
  if (profile.originalArtwork && profile.originalArtwork.url) return false;

  const form = new FormData();
  form.append('original', held.blob, held.fileName);
  try {
    const res = await fetch(ARTWORK_ENDPOINT, { method: 'POST', body: form });
    const data = await res.json();
    if (!data || !data.ok || !data.original || !data.original.stored) return false;
    /* ⚠ ON THE PROFILE, not on the logo element — the element is rebuilt by the very choreography
       that runs on the line after this is fired, and by every later layout change. See
       `design-profile.js`'s `originalArtwork`. */
    profile.originalArtwork = {
      url: data.original.url,
      fileName: data.original.fileName || held.fileName,
    };
    return true;
  } catch {
    return false;
  }
}

/**
 * Store whichever of the two files this session actually has, and hand back the signed paths.
 *
 * Best-effort by design, exactly as the quote path treats it: the caller has a document to produce
 * either way, and a sheet that says "not on file" is a far better outcome than no sheet. Falls back
 * to the path already pinned on the design when the bytes are gone. Never throws.
 *
 * @returns {Promise<{originalUrl: string|null, enhancedUrl: string|null,
 *                    originalName: string, enhancedName: string}>}
 */
export async function uploadDesignArtwork(state) {
  const empty = {
    originalUrl: null, enhancedUrl: null, originalName: '', enhancedName: '',
    printUrl: null, printPx: '', printDpi: '',
  };
  const held = retained();
  const logoEl = (state?.designProfile?.elements || [])
    .find((e) => e && e.type === 'logo' && e.logo && e.logo.dataUrl);
  const enhancedDataUrl = logoEl ? logoEl.logo.dataUrl : null;
  /* ⚠ THE DESIGN MAY ALREADY KNOW WHERE ITS ORIGINAL IS. `retainOriginalForRep` pins the path onto
     the PROFILE at upload, and that pointer survives a relayout, a save, a reopen and a share link
     — which is the whole point, because the HOLDER survives none of them. Read it before deciding
     there is nothing to offer. */
  const pin = state?.designProfile?.originalArtwork || null;
  const pinned = (pin && pin.url) || null;
  const pinnedName = (pin && pin.fileName) || '';
  if (!held && !enhancedDataUrl) return pinned ? { ...empty, originalUrl: pinned, originalName: pinnedName } : empty;

  const form = new FormData();
  // Only when the bytes are actually in hand — a pinned path needs no re-upload.
  if (held) form.append('original', held.blob, held.fileName);
  if (enhancedDataUrl) {
    /* `fetch` on a data: URL is the cheapest correct decode to a Blob and needs no canvas
       round-trip — which would re-encode the very file we are keeping as the record. Same
       reasoning, same two lines, as the quote path. */
    try {
      const blob = await (await fetch(enhancedDataUrl)).blob();
      form.append('enhanced', blob, 'enhanced.png');
    } catch { /* the original is the one that matters — carry on without this */ }
  }

  /* THE PRINT ASSET — the only one the vendor sheet will name (2026-09-02). Built here rather
     than at upload because it is sized from the DESIGN: the same logo printing 9in on a bumper
     and 3in on a dumbbell head does not need the same file, and at upload neither the product
     nor the size dial has been settled. Lazy-imported so a buyer's session never pays for a
     module only the vendor sheet uses.
       ⚠ NULL IS A NORMAL ANSWER and every field below is written for its absence — no source,
     a declined upscale, a spend cap, an offline worker. The sheet then links the enhanced copy
     exactly as it did before, which is the behaviour this replaces rather than a failure. */
  let print = null;
  try {
    const { buildPrintArtwork } = await import('./print-artwork.js');
    print = await buildPrintArtwork(state);
    if (print && print.blob) form.append('print', print.blob, 'print.png');
  } catch { /* best-effort, like everything else on this path */ }

  try {
    const res = await fetch(ARTWORK_ENDPOINT, { method: 'POST', body: form });
    const data = await res.json();
    if (!data || !data.ok) return empty;
    return {
      originalUrl: (data.original && data.original.stored ? data.original.url : null) || pinned,
      enhancedUrl: data.enhanced && data.enhanced.stored ? data.enhanced.url : null,
      printUrl: data.print && data.print.stored ? data.print.url : null,
      originalName: (data.original && data.original.fileName) || (held ? held.fileName : '') || pinnedName,
      enhancedName: (data.enhanced && data.enhanced.fileName) || '',
      // Printed beside the link so nobody opens the file to find out whether it is big enough.
      printPx: print ? print.px : '',
      printDpi: print ? print.dpiLabel : '',
    };
  } catch {
    return pinned ? { ...empty, originalUrl: pinned, originalName: pinnedName } : empty;
  }
}
